/**
 * This store should store states that directly reflect a user's project design choices
 * These states capture user decisions, such as the fit/fill state of the canvas background, text specifications, sticker selection etc.
 * States related to a specific project should be retrievable from this store for features such as project saving
 */

import { defineStore } from 'pinia'
import { Resolution, LogoState, Orientation, StickerState, TextState } from '@/types'
import { EffectGroupName, EffectName } from '@/static/effectName'
import useIdGenerator from '@/composables/UseIdGenerator'
import { Layers } from '@/static/Layers'
import { EffectState } from '@/pixi/filters/types'

export type ScaleType = 'fill' | 'fit'

export const resolutionSizes: Record<Orientation, Resolution> = {
  'landscape': { width: 1920, height: 1080 },
  'square': { width: 1080, height: 1080 },
  'portrait': { width: 1080, height: 1920 }
}
export interface BackgroundState {
  backgroundScale: ScaleType
  backgroundUrl: string
  backgroundFileName: string | null
}
export interface ProjectState {
  projectId: number
  background: BackgroundState
  stickers: StickerState[]
  logoStickers: LogoState[]
  textStates: TextState[]
  orientation: Orientation,
  selectedEffect: EffectState,
  activeEffectStates: Map<EffectGroupName, EffectState>
  countLockedEffect: number
}

const { getId } = useIdGenerator()

const defaultEffectState: EffectState = {
  name: EffectName.NONE,
  groupName: EffectGroupName.NONE,
  appliedToLayer: [Layers.BACKGROUND, Layers.STICKER, Layers.LOGO, Layers.TEXT],
  intensity: 0.5,
  audioReactivityEnabled: false,
  isLocked: false
}

export const useProjectStore = defineStore('project', {
  state: (): ProjectState => ({
    projectId: getId(),
    background: {
      backgroundScale: 'fill',
      backgroundUrl: 'https://static.serato.com/visualizer/videos/swirly-purple-forms-compressed.mp4',
      backgroundFileName: null
    },
    stickers: [],
    logoStickers: [],
    textStates: [],
    orientation: 'portrait',
    selectedEffect: JSON.parse(JSON.stringify(defaultEffectState)),
    activeEffectStates: new Map<EffectGroupName, EffectState>(),
    countLockedEffect: 0
  }),
  getters: {
    resolution: (state): Resolution => resolutionSizes[state.orientation]
  },
  actions: {
    setBackground (url: string, scale: ScaleType = 'fill', fileName: string | null = null): void {
      this.background.backgroundUrl = url
      this.background.backgroundScale = scale
      this.background.backgroundFileName = fileName
    },
    toggleBackgroundScale (): void {
      this.background.backgroundScale = this.background.backgroundScale === 'fill' ? 'fit' : 'fill'
    },
    toggleLock (effectGroupName: EffectGroupName): void {
      const effectState = this.activeEffectStates.get(effectGroupName)
      if (effectState) {
        effectState.isLocked = !effectState.isLocked
        this.countLockedEffect = [...this.activeEffectStates.values()].filter((effect) => effect.isLocked === true).length
      }
    }
  }
})
